import { useLogger } from "@kensho/lumberjack";
import { useCallback, useMemo } from "react";

import { CookieConsentOptions } from "../components/cookie-banner/cookie-consent-dialog-contents";
import { CONSENT_COOKIE_DEFAULTS, CONSENT_COOKIE_NAME } from "../constants";
import { useCookie } from "../hooks/use-cookie";
import type {
  AnalyticsContextInterface,
  AnalyticsEventDetails,
  AnalyticsProvider,
} from "./site-analytics-context";
import { SiteAnalyticsContext } from "./site-analytics-context";

const analyticsProviders: AnalyticsProvider[] = [
  {
    name: "Plausible",
    consentRequiredToEnable: {
      functional: false,
      targeting: false,
      performance: false,
      isSet: false,
    },
    enable: (): void => {},
    disable: (): void => {},
    sendEvent: (eventName: string, details?: AnalyticsEventDetails): void => {
      window.plausible(eventName, { props: details });
    },
  },
];

const checkConsent = (
  baseConsent: Partial<CookieConsentOptions>,
  testConsent: Partial<CookieConsentOptions>,
): boolean => {
  let modifiedBaseConsent = baseConsent;
  if (!baseConsent.isSet) {
    modifiedBaseConsent = {
      functional: false,
      targeting: false,
      performance: false,
      isSet: false,
    };
  }

  return Object.entries(testConsent).every(
    ([key, value]) =>
      !value || modifiedBaseConsent[key as keyof typeof testConsent],
  );
};

export function submitConsent(
  consentUpdate: Partial<CookieConsentOptions>,
): void {
  analyticsProviders.forEach((provider) =>
    checkConsent(consentUpdate, provider.consentRequiredToEnable)
      ? provider.enable()
      : provider.disable(),
  );
}

export function SiteAnalyticsProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const log = useLogger();

  const [consentCookie, setConsentCookie] = useCookie<CookieConsentOptions>(
    CONSENT_COOKIE_NAME,
    CONSENT_COOKIE_DEFAULTS,
  );

  const setConsent = useCallback(
    (cookie: CookieConsentOptions): void => {
      setConsentCookie(cookie);
      submitConsent(cookie);
    },
    [setConsentCookie],
  );

  const sendEvent = useCallback(
    (
      eventName: string,
      eventDetails?: AnalyticsEventDetails,
      consentFilter: Partial<CookieConsentOptions> = { performance: true },
    ): void => {
      analyticsProviders.forEach((provider) => {
        if (!checkConsent(consentCookie, consentFilter)) return;
        try {
          provider.sendEvent?.(eventName, eventDetails);
        } catch (originalError) {
          const error = new Error("Failed to send analytics event");
          error.cause = originalError;
          log.warning(error, { eventName, provider: provider.name });
        }
      });
    },
    [consentCookie, log],
  );

  const setSessionContext = useCallback(
    (
      name: string,
      key: string,
      value: string,
      consentFilter: Partial<CookieConsentOptions> = { performance: true },
    ): void => {
      analyticsProviders.forEach((provider) => {
        if (!checkConsent(consentCookie, consentFilter)) return;
        if (provider.setSessionContext)
          provider.setSessionContext(name, key, value);
      });
    },
    [consentCookie],
  );

  const value = useMemo<AnalyticsContextInterface>(
    () => ({
      consent: consentCookie,
      setConsent,
      sendEvent,
      setSessionContext,
    }),
    [consentCookie, setConsent, sendEvent, setSessionContext],
  );

  return <SiteAnalyticsContext value={value}>{children}</SiteAnalyticsContext>;
}
